/**
 * `/app/market` — Coinglass-style market dashboard (PULSE-211).
 *
 * One symbol, one timeframe → a stacked multi-pane chart with price +
 * cross-exchange aggregate OI / Net Delta / L-S / liquidations / funding.
 * The liq heatmap (PULSE-210) is added below in P2.
 *
 * Admin-gated (mirrors Screeners): operator analysis tooling.
 */
import { useMemo, useState } from 'react'
import { MarketChart, ChartViewToggle, useChartView, CG_VIEW_OMIT } from '@/components/market/MarketChart'
import { HeatmapPanel } from '@/components/market/HeatmapPanel'
import {
  useMarketSymbols, useMarketSeries, useMarketHeatmap, TIMEFRAMES, type Timeframe,
} from '@/hooks/useMarketApi'
import { useCreateCoinShare } from '@/hooks/useDashboardApi'
import { formatUsd, formatDateTime } from '@/lib/format'

const LEGEND: { label: string; varName: string; fallback: string }[] = [
  { label: 'Price / Vol', varName: '--text', fallback: '#E6EAF0' },
  { label: 'OI (agg)', varName: '--sky', fallback: '#38BDF8' },
  { label: 'CVD (agg)', varName: '--amber', fallback: '#F59E0B' },
  { label: 'Net Delta Δ', varName: '--pulse', fallback: '#84CC16' },
  { label: 'Taker buy/sell + Δ', varName: '--pulse', fallback: '#84CC16' },
  { label: 'L/S ratio', varName: '--indigo', fallback: '#6366F1' },
  { label: 'Liq long', varName: '--rose', fallback: '#F43F5E' },
  { label: 'Liq short', varName: '--pulse', fallback: '#84CC16' },
]

export default function Market() {
  const [symbol, setSymbol] = useState('BTCUSDT')
  const [tf, setTf] = useState<Timeframe>('5m')
  const [view, setView] = useChartView()

  const symbols = useMarketSymbols()
  const series = useMarketSeries(symbol, tf, 300)
  const heatmap = useMarketHeatmap(symbol, tf, 300)

  const coinShare = useCreateCoinShare()
  const [shared, setShared] = useState(false)
  const onShare = () => coinShare.mutate(symbol, {
    onSuccess: (d) => {
      navigator.clipboard?.writeText(window.location.origin + d.url)
      setShared(true); setTimeout(() => setShared(false), 1600)
    },
  })

  const last = useMemo(() => {
    const d = series.data
    if (!d) return null
    const c = d.candles.at(-1)
    const oi = d.oi.at(-1)
    const lsr = d.lsr.at(-1)
    const fund = d.funding.at(-1)
    return {
      price: c?.c ?? null,
      change: c && d.candles[0] ? ((c.c - d.candles[0].o) / d.candles[0].o) * 100 : null,
      oi: oi?.v ?? null,
      nEx: oi?.n_ex ?? null,
      ratio: lsr?.ratio ?? null,
      funding: fund ? fund.v * 100 : null,
    }
  }, [series.data])

  return (
    <div className="p-4 sm:p-6 max-w-[1500px]">
      {/* Header */}
      <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-textFaint uppercase tracking-wider mb-1">Market</div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {symbol}
            {last?.price != null && (
              <span className="ml-3 font-mono text-lg text-textDim">
                {last.price.toLocaleString('en-US', { maximumFractionDigits: 6 })}
              </span>
            )}
            {last?.change != null && (
              <span className={`ml-2 text-sm font-mono ${last.change >= 0 ? 'text-pulse' : 'text-rose'}`}>
                {last.change >= 0 ? '+' : ''}{last.change.toFixed(2)}%
              </span>
            )}
          </h1>
          {series.data?.active?.is_active === false && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-amber bg-[var(--amber-faint)] border border-amber/30 rounded px-2 py-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber animate-pulse" />
              Символ неактивен — свечи / тик-цена / L/S на паузе
              {series.data.active.last_seen != null && (
                <span className="text-textDim">
                  (с {formatDateTime(new Date(series.data.active.last_seen * 1000))})
                </span>
              )}
              <span className="text-textFaint">· OI / CVD / ликвидации идут</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <ChartViewToggle view={view} onChange={setView} />
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="bg-elevated border border-line rounded px-3 py-1.5 text-sm font-mono focus:border-fire outline-none"
          >
            {!symbols.data?.some((s) => s.symbol === symbol) && (
              <option value={symbol}>{symbol}</option>
            )}
            {symbols.data?.map((s) => (
              <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
            ))}
          </select>

          <div className="flex border border-line rounded overflow-hidden">
            {TIMEFRAMES.map((f) => (
              <button
                key={f}
                onClick={() => setTf(f)}
                className={`px-3 py-1.5 text-xs font-mono transition-colors ${
                  tf === f ? 'bg-fire-faint text-fire' : 'text-textDim hover:text-text hover:bg-elevated'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <button
            onClick={onShare}
            disabled={coinShare.isPending}
            title="Скопировать публичную ссылку на сводку по монете"
            className="px-3 py-1.5 text-xs border border-line rounded text-textDim hover:text-pulse hover:border-pulse/50 transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {shared ? '✓ ссылка скопирована' : coinShare.isPending ? '…' : 'Поделиться'}
          </button>
        </div>
      </div>

      {/* Top stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-line mb-3 text-sm">
        <Stat label="Aggregate OI" value={last?.oi != null ? formatUsd(last.oi, { compact: true }) : '—'}
              sub={last?.nEx != null ? `${last.nEx} venues` : undefined} />
        <Stat label="L/S ratio" value={last?.ratio != null ? last.ratio.toFixed(2) : '—'}
              tone={last?.ratio != null ? (last.ratio >= 1 ? 'pulse' : 'rose') : undefined} />
        <Stat label="Funding (avg)" value={last?.funding != null ? `${last.funding.toFixed(4)}%` : '—'}
              tone={last?.funding != null ? (last.funding >= 0 ? 'pulse' : 'rose') : undefined} />
        <Stat label="Timeframe" value={tf.toUpperCase()} sub={`${series.data?.candles.length ?? 0} bars`} />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-2 text-xs text-textDim flex-wrap">
        {LEGEND.map((l) => (
          <span key={l.label} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: `var(${l.varName}, ${l.fallback})` }} />
            {l.label}
          </span>
        ))}
        <span className="ml-auto text-textFaint">auto-refresh 30s · panes resizable</span>
      </div>

      {/* Chart */}
      <div className="border border-line rounded bg-surface relative" style={{ height: 780 }}>
        {series.isLoading && (
          <div className="absolute inset-0 grid place-items-center text-textDim text-sm">Loading…</div>
        )}
        {series.isError && (
          <div className="absolute inset-0 grid place-items-center text-rose text-sm">
            Failed to load series.
          </div>
        )}
        {series.data && series.data.candles.length === 0 && (
          <div className="absolute inset-0 grid place-items-center text-textDim text-sm">
            No data for {symbol} / {tf}.
          </div>
        )}
        {series.data && series.data.candles.length > 0 && (
          <MarketChart key={view} data={series.data} omit={view === 'cg' ? CG_VIEW_OMIT : []} />
        )}
      </div>

      {/* Liquidation heatmap (PULSE-210) — same window, below the chart */}
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-textFaint uppercase tracking-wider">
          Liquidation heatmap
          {heatmap.data?.n_ex != null && (
            <span className="ml-2 normal-case text-textFaint">агрегат OI · {heatmap.data.n_ex} бирж</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-textFaint">
          <span>низкая</span>
          <span className="inline-block h-2 w-28 rounded-sm"
                style={{ background: 'linear-gradient(90deg,#440154,#3b528b,#21918c,#3cae6e,#94d743,#fde725)' }} />
          <span>высокая</span>
        </div>
      </div>
      <div className="border border-line rounded bg-surface relative overflow-hidden" style={{ height: 320 }}>
        {heatmap.isLoading && (
          <div className="absolute inset-0 grid place-items-center text-textDim text-sm">Building heatmap…</div>
        )}
        {heatmap.isError && (
          <div className="absolute inset-0 grid place-items-center text-rose text-sm">Failed to build heatmap.</div>
        )}
        {heatmap.data && <HeatmapPanel data={heatmap.data} candles={series.data?.candles} />}
      </div>
      <div className="mt-2 text-xs text-textFaint">
        Реконструкция из агрегированного OI 12 бирж (модель тиров плеча, PULSE-210). Светлые полосы = ценовые
        уровни с наибольшим объёмом потенциальных ликвидаций — «магниты». Сверять с Coinglass.
      </div>
    </div>
  )
}

function Stat({
  label, value, sub, tone,
}: { label: string; value: string; sub?: string; tone?: 'pulse' | 'rose' }) {
  const toneCls = tone === 'pulse' ? 'text-pulse' : tone === 'rose' ? 'text-rose' : 'text-text'
  return (
    <div className="bg-surface px-4 py-2.5">
      <div className="text-xs text-textFaint uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-base ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-textFaint">{sub}</div>}
    </div>
  )
}
